import type { ResizableOrientation } from './_resizable-layout.js'
import { ContextConsumer } from '@lit/context'
import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLightDomStyles, setLightDomScope } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import {
  requestResizableContextAfterParentUpgrade,
  resizableContext,
} from './_ResizableContext.js'
import style from './Resizable.light-dom.css'

const scopeKey = 'nord-resizable'

function roundAriaValueNow(value: number | undefined): number | undefined {
  return value === undefined ? undefined : Number(value.toFixed(2))
}

export interface ResizableHandleGroupState {
  readonly disabled: boolean
  readonly orientation: ResizableOrientation
  readonly controls?: string
  readonly valueMin?: number
  readonly valueMax?: number
  readonly valueNow?: number
  readonly valueText?: string
}

/**
 * Resizable Handle is an accessible window splitter placed between two direct
 * Resizable Panels.
 *
 * @status draft
 * @category layout
 * @parent Resizable
 * @lightDom true
 *
 * @localization resizePanels - Default accessible name for the separator.
 *
 * @cssprop [--n-resizable-handle-color=var(--n-color-border)] - Controls the divider color.
 * @cssprop [--n-resizable-handle-active-color=var(--n-color-accent)] - Controls hover, focus, and drag color.
 * @cssprop [--n-resizable-handle-target-size=calc(var(--n-space-xl) + var(--n-space-s))] - Controls the interactive hit target without changing the layout track.
 */
@customElement('nord-resizable-handle')
export default class ResizableHandle extends DraftComponentMixin(LitElement) {
  /** Disables pointer and keyboard resizing and removes the handle from tab order. */
  @property({ reflect: true, type: Boolean }) disabled = false

  /** Shows a centered Nord-token grip in addition to the minimal divider. */
  @property({ attribute: 'with-handle', reflect: true, type: Boolean }) withHandle = false

  /** Accessible label. Defaults to a localized “Resize panels”. */
  @property({ reflect: true }) label = ''

  private groupState: ResizableHandleGroupState = {
    disabled: true,
    orientation: 'horizontal',
  }

  private context = new ContextConsumer(this, {
    context: resizableContext,
    subscribe: true,
    callback: context => context?.registerHandle(this),
  })

  private localization = new LocalizeController<'nord-resizable-handle'>(this)
  private grip?: HTMLSpanElement

  get interactionDisabled(): boolean {
    return this.disabled || this.groupState.disabled
  }

  connectedCallback() {
    super.connectedCallback()
    this.dataset.nResizableHandle = ''
    injectLightDomStyles(this, style, scopeKey)
    this.ensureGrip()
    this.addEventListener('pointerdown', this.handlePointerDown)
    this.addEventListener('pointermove', this.handlePointerMove)
    this.addEventListener('pointerup', this.handlePointerEnd)
    this.addEventListener('pointercancel', this.handlePointerEnd)
    this.addEventListener('lostpointercapture', this.handlePointerEnd)
    this.addEventListener('keydown', this.handleKeyDown)
    this.context.value?.registerHandle(this)
    if (!this.context.value) {
      requestResizableContextAfterParentUpgrade(this, () => this.context.hostConnected())
    }
    this.syncSemantics()
  }

  disconnectedCallback() {
    this.context.value?.cancelHandleInteraction(this)
    this.context.value?.unregisterHandle(this)
    this.removeEventListener('pointerdown', this.handlePointerDown)
    this.removeEventListener('pointermove', this.handlePointerMove)
    this.removeEventListener('pointerup', this.handlePointerEnd)
    this.removeEventListener('pointercancel', this.handlePointerEnd)
    this.removeEventListener('lostpointercapture', this.handlePointerEnd)
    this.removeEventListener('keydown', this.handleKeyDown)
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }

  protected updated() {
    this.ensureGrip()
    this.syncSemantics()
    if (this.disabled) {
      this.context.value?.cancelHandleInteraction(this)
    }
    this.context.value?.notifyHandle(this)
  }

  /** @internal */
  setGroupState(state: ResizableHandleGroupState) {
    this.groupState = state
    this.syncSemantics()
  }

  private ensureGrip() {
    if (!this.grip) {
      this.grip = document.createElement('span')
      this.grip.dataset.nResizableHandleGrip = ''
      this.grip.setAttribute('aria-hidden', 'true')
      setLightDomScope(scopeKey, this.grip)
      this.append(this.grip)
    }
    this.grip.hidden = !this.withHandle
    this.grip.toggleAttribute('data-n-resizable-handle-grip-hidden', !this.withHandle)
  }

  private syncSemantics() {
    const {
      controls,
      orientation,
      valueMax,
      valueMin,
      valueNow,
      valueText,
    } = this.groupState
    const disabled = this.interactionDisabled
    this.setAttribute('role', 'separator')
    this.tabIndex = disabled ? -1 : 0
    this.setAttribute('aria-disabled', String(disabled))
    this.setAttribute('aria-orientation', orientation === 'horizontal' ? 'vertical' : 'horizontal')
    this.setAttribute('aria-label', this.label || this.localization.term('resizePanels'))
    this.setOptionalAttribute('aria-controls', controls)
    this.setOptionalAttribute('aria-valuemin', valueMin)
    this.setOptionalAttribute('aria-valuemax', valueMax)
    this.setOptionalAttribute('aria-valuenow', roundAriaValueNow(valueNow))
    this.setOptionalAttribute('aria-valuetext', valueText)
  }

  private setOptionalAttribute(name: string, value: string | number | undefined) {
    if (value === undefined || value === '') {
      this.removeAttribute(name)
    }
    else {
      this.setAttribute(name, String(value))
    }
  }

  private handlePointerDown = (event: PointerEvent) => {
    this.context.value?.pointerDown(this, event)
  }

  private handlePointerMove = (event: PointerEvent) => {
    this.context.value?.pointerMove(this, event)
  }

  private handlePointerEnd = (event: PointerEvent) => {
    this.context.value?.pointerEnd(this, event)
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    this.context.value?.keyboardResize(this, event)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-resizable-handle': ResizableHandle
  }
}
